// app-tour.component.ts
import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TourService } from '../services/tour.service';
import { Subscription } from 'rxjs';
import { AppTourService } from '../services/app-tour.service';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-tour',
  standalone: true,
  imports: [CommonModule],
  template: `
    <ng-container *ngIf="showButton">
      <button
        (click)="startTour()"
        [class]="buttonClass"
        [attr.aria-label]="buttonAriaLabel">
        <ng-container *ngIf="showIcon">
          <svg *ngIf="!tourActive" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 mr-1">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
          </svg>
          <svg *ngIf="tourActive" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 mr-1">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </ng-container>
        {{ tourActive ? stopText : startText }}
      </button>
    </ng-container>
  `,
  styles: []
})
export class TourComponent implements OnInit, OnDestroy {
  @Input() tourName: string = 'default-tour';
  @Input() autoStart: boolean = false;
  @Input() showOnlyOnce: boolean = true;
  @Input() showButton: boolean = true;
  @Input() showIcon: boolean = true;
  @Input() startText: string = 'Take a Tour';
  @Input() stopText: string = 'Stop Tour';
  @Input() buttonClass: string = 'btn btn-outline-action';
  @Input() buttonAriaLabel: string = 'Start a guided tour';

  tourActive: boolean = false;
  private subscription: Subscription = new Subscription();

  constructor(
    private tourService: TourService,
    private appTourService: AppTourService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Subscribe to tour active state
    this.subscription.add(
      this.tourService.tourActive$.subscribe(active => {
        this.tourActive = active;
      })
    );

    // Monitor route changes
    this.subscription.add(
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe(() => {
        // Check if this is the right tour component for this route
        if (this.isTourComponentForCurrentRoute()) {
          console.log(`Tour component for "${this.tourName}" active on route: ${this.router.url}`);

          // When route changes complete, initialize the tour for this page
          this.initializeTourForCurrentPage();
        }
      })
    );

    // Also initialize on initial load
    setTimeout(() => {
      if (this.isTourComponentForCurrentRoute()) {
        console.log(`Tour component for "${this.tourName}" initializing on startup`);
        this.initializeTourForCurrentPage();
      }
    }, 1000);
  }

  /**
   * Initialize tour for the current page
   */
  private initializeTourForCurrentPage(): void {
    // If auto-start is enabled and tour isn't completed, start it
    if (this.autoStart && !this.appTourService.isTourCompleted()) {
      console.log(`Auto-starting tour for ${this.tourName}`);
      setTimeout(() => this.startTour(), 800);
    }
  }

  /**
   * Check if this is the correct tour component for the current route
   */
  private isTourComponentForCurrentRoute(): boolean {
    const currentRoute = this.router.url.split('?')[0].split('#')[0];

    // Match tourName with route
    if (this.tourName === 'dashboard' &&
      (currentRoute === '/' || currentRoute === '/dashboard' || currentRoute === '')) {
      return true;
    }

    if (this.tourName === 'icp-optimization' &&
      currentRoute === '/icp-optimization') {
      return true;
    }

    return false;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  startTour(): void {
    // Use the appTourService to start the tour
    this.appTourService.startTour();
  }

  resetTourStatus(): void {
    this.tourService.resetTourStatus(this.tourName);
  }
}
